import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getSupplier, updateSupplier, deleteSupplier } from './api';
const CATEGORIES = [
    'Glass',
    'Hardware',
    'Frames',
    'Sealants & Adhesives',
    'Consumables',
    'Tools & Equipment',
    'Other',
];
const PAYMENT_TERMS = ['Net 30', 'Net 60', 'Net 90', 'COD', 'Due on Receipt', 'Other'];
export const SupplierDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [supplier, setSupplier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const load = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const s = await getSupplier(id);
            setSupplier(s);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load supplier');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    const startEdit = () => {
        if (!supplier)
            return;
        setDraft({ ...supplier });
        setSaveError(null);
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setDraft({});
        setSaveError(null);
    };
    const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
    const setAddress = (field, value) => setDraft((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
    const handleSave = async () => {
        if (!id || !draft.name?.trim()) {
            setSaveError('Company name is required.');
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateSupplier(id, draft);
            setSupplier(updated);
            setEditing(false);
        }
        catch (e) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const handleDelete = async () => {
        if (!id)
            return;
        setDeleting(true);
        try {
            await deleteSupplier(id);
            navigate('/suppliers');
        }
        catch (e) {
            setError(e.message ?? 'Failed to delete');
            setDeleting(false);
            setConfirmDelete(false);
        }
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error || !supplier) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.errorBanner, children: error || 'Supplier not found' }) }));
    }
    const containerStyle = {
        maxWidth: isMobile ? '100%' : '720px',
        margin: '0 auto',
    };
    const data = editing ? draft : supplier;
    const field = (label, value, fullWidth = false) => (_jsxs("div", { style: fullWidth ? styles.field : undefined, children: [_jsx("span", { style: styles.fieldLabel, children: label }), _jsx("span", { style: styles.fieldValue, children: value || _jsx("span", { style: styles.empty, children: "\u2014" }) })] }));
    const editInput = (label, fieldKey, type = 'text', placeholder = '') => (_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: label }), _jsx("input", { type: type, style: styles.input, value: draft[fieldKey] || '', onChange: (e) => setField(fieldKey, e.target.value || undefined), placeholder: placeholder, disabled: saving })] }));
    return (_jsx(Layout, { children: _jsxs("div", { style: containerStyle, children: [_jsxs("div", { style: styles.pageHeader, children: [_jsx("button", { style: styles.backButton, onClick: () => navigate('/suppliers'), children: "\u2190 Suppliers" }), _jsxs("div", { style: styles.headerRow, children: [_jsxs("div", { children: [_jsxs("div", { style: styles.headerIdRow, children: [_jsx("span", { style: styles.supplierId, children: supplier.id }), _jsx("span", { style: {
                                                        ...styles.statusBadge,
                                                        backgroundColor: supplier.status === 'active'
                                                            ? 'rgba(52,199,89,0.12)' : 'rgba(142,142,147,0.15)',
                                                        color: supplier.status === 'active' ? '#248a3d' : '#6e6e73',
                                                    }, children: supplier.status === 'active' ? 'Active' : 'Inactive' })] }), _jsx("h2", { style: styles.heading, children: supplier.name }), supplier.category && _jsx("p", { style: styles.category, children: supplier.category })] }), !editing && (_jsx("div", { style: styles.headerActions, children: _jsx("button", { style: styles.editButton, onClick: startEdit, children: "Edit" }) }))] })] }), editing ? (
                /* Edit mode */
                _jsxs("div", { children: [_jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Company Info" }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsxs("label", { style: styles.label, children: ["Company Name ", _jsx("span", { style: styles.req, children: "*" })] }), _jsx("input", { type: "text", style: styles.input, value: draft.name || '', onChange: (e) => setField('name', e.target.value), disabled: saving })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "Status" }), _jsxs("select", { style: styles.select, value: draft.status || 'active', onChange: (e) => setField('status', e.target.value), disabled: saving, children: [_jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Category" }), _jsxs("select", { style: styles.select, value: draft.category || '', onChange: (e) => setField('category', e.target.value || undefined), disabled: saving, children: [_jsx("option", { value: "", children: "Select category\u2026" }), CATEGORIES.map((c) => _jsx("option", { value: c, children: c }, c))] })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Account Number" }), _jsx("input", { type: "text", style: styles.input, value: draft.accountNumber || '', onChange: (e) => setField('accountNumber', e.target.value || undefined), placeholder: "Account #", disabled: saving })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Tax ID" }), _jsx("input", { type: "text", style: styles.input, value: draft.taxId || '', onChange: (e) => setField('taxId', e.target.value || undefined), placeholder: "EIN / Tax ID", disabled: saving })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Payment Terms" }), _jsxs("select", { style: styles.select, value: draft.paymentTerms || '', onChange: (e) => setField('paymentTerms', e.target.value || undefined), disabled: saving, children: [_jsx("option", { value: "", children: "Select terms\u2026" }), PAYMENT_TERMS.map((t) => _jsx("option", { value: t, children: t }, t))] })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Website" }), _jsx("input", { type: "url", style: styles.input, value: draft.website || '', onChange: (e) => setField('website', e.target.value || undefined), placeholder: "https://\u2026", disabled: saving })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "Credit Limit ($)" }), _jsx("input", { type: "number", style: styles.input, value: draft.creditLimit ?? '', onChange: (e) => setField('creditLimit', e.target.value ? Number(e.target.value) : undefined), placeholder: "0.00", min: "0", step: "0.01", disabled: saving })] })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Contact" }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "First Name" }), _jsx("input", { type: "text", style: styles.input, value: draft.contactFirstName || '', onChange: (e) => setField('contactFirstName', e.target.value || undefined), placeholder: "First name", disabled: saving })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Last Name" }), _jsx("input", { type: "text", style: styles.input, value: draft.contactLastName || '', onChange: (e) => setField('contactLastName', e.target.value || undefined), placeholder: "Last name", disabled: saving })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Phone" }), _jsx("input", { type: "tel", style: styles.input, value: draft.phone || '', onChange: (e) => setField('phone', e.target.value || undefined), placeholder: "(555) 555-5555", disabled: saving })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "AP Email" }), _jsx("input", { type: "email", style: styles.input, value: draft.apEmail || '', onChange: (e) => setField('apEmail', e.target.value || undefined), placeholder: "accounts@supplier.com", disabled: saving })] })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "AR Email" }), _jsx("input", { type: "email", style: styles.input, value: draft.arEmail || '', onChange: (e) => setField('arEmail', e.target.value || undefined), placeholder: "ar@supplier.com", disabled: saving })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Address" }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Street" }), _jsx("input", { type: "text", style: styles.input, value: draft.address?.street || '', onChange: (e) => setAddress('street', e.target.value), placeholder: "123 Main St", disabled: saving })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "City" }), _jsx("input", { type: "text", style: styles.input, value: draft.address?.city || '', onChange: (e) => setAddress('city', e.target.value), placeholder: "City", disabled: saving })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "State" }), _jsx("input", { type: "text", style: styles.input, value: draft.address?.state || '', onChange: (e) => setAddress('state', e.target.value), placeholder: "CA", maxLength: 2, disabled: saving })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "ZIP" }), _jsx("input", { type: "text", style: styles.input, value: draft.address?.zip || '', onChange: (e) => setAddress('zip', e.target.value), placeholder: "12345", maxLength: 10, disabled: saving })] })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Notes" }), _jsx("textarea", { style: styles.textarea, value: draft.notes || '', onChange: (e) => setField('notes', e.target.value || undefined), placeholder: "General notes\u2026", rows: 4, disabled: saving })] }), saveError && _jsx("div", { style: styles.errorBanner, children: saveError }), _jsxs("div", { style: styles.editActions, children: [_jsx("button", { style: styles.cancelButton, onClick: cancelEdit, disabled: saving, children: "Cancel" }), _jsx("button", { style: { ...styles.saveButton, opacity: saving ? 0.6 : 1 }, onClick: handleSave, disabled: saving, children: saving ? 'Saving…' : 'Save Changes' })] })] })) : (
                /* View mode */
                _jsxs("div", { children: [_jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Company Info" }), _jsxs("div", { style: isMobile ? undefined : styles.grid2, children: [field('Account Number', supplier.accountNumber), field('Tax ID', supplier.taxId), field('Payment Terms', supplier.paymentTerms), field('Credit Limit', supplier.creditLimit != null ? `$${supplier.creditLimit.toLocaleString()}` : undefined), field('Website', supplier.website)] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Contact" }), _jsxs("div", { style: isMobile ? undefined : styles.grid2, children: [field('Name', [supplier.contactFirstName, supplier.contactLastName].filter(Boolean).join(' ') || undefined), field('Phone', supplier.phone), field('AP Email', supplier.apEmail), field('AR Email', supplier.arEmail)] })] }), (supplier.address?.street || supplier.address?.city) && (_jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Address" }), _jsxs("p", { style: styles.addressText, children: [supplier.address.street && _jsxs(_Fragment, { children: [supplier.address.street, _jsx("br", {})] }), [supplier.address.city, supplier.address.state].filter(Boolean).join(', '), supplier.address.zip && ` ${supplier.address.zip}`] })] })), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Notes" }), supplier.notes ? (_jsx("p", { style: styles.notesText, children: supplier.notes })) : (_jsx("p", { style: styles.empty, children: "No notes" }))] }), _jsxs("div", { style: { ...styles.section, borderColor: 'rgba(255,59,48,0.2)' }, children: [_jsx("h3", { style: { ...styles.sectionTitle, color: '#c0392b' }, children: "Danger Zone" }), confirmDelete ? (_jsxs("div", { children: [_jsxs("p", { style: { fontSize: '14px', color: '#3c3c43', marginBottom: '12px' }, children: ["Are you sure you want to delete ", _jsx("strong", { children: supplier.name }), "? This cannot be undone."] }), _jsxs("div", { style: { display: 'flex', gap: '10px' }, children: [_jsx("button", { style: styles.cancelButton, onClick: () => setConfirmDelete(false), disabled: deleting, children: "Cancel" }), _jsx("button", { style: { ...styles.deleteConfirmButton, opacity: deleting ? 0.6 : 1 }, onClick: handleDelete, disabled: deleting, children: deleting ? 'Deleting…' : 'Yes, Delete' })] })] })) : (_jsx("button", { style: styles.deleteButton, onClick: () => setConfirmDelete(true), children: "Delete Supplier" }))] })] }))] }) }));
};
const styles = {
    loadingCenter: { display: 'flex', justifyContent: 'center', padding: '80px 0' },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    pageHeader: { marginBottom: '24px' },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 8px 0',
        display: 'block',
        minHeight: '44px',
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerIdRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '4px',
    },
    supplierId: {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#86868b',
        fontWeight: '600',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '980px',
        fontSize: '11px',
        fontWeight: '700',
    },
    heading: {
        fontSize: '34px',
        fontWeight: '700',
        margin: '0 0 4px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    category: {
        fontSize: '15px',
        color: '#6e6e73',
        margin: 0,
    },
    headerActions: { display: 'flex', gap: '10px', paddingTop: '4px' },
    editButton: {
        padding: '8px 20px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        color: '#1d1d1f',
        minHeight: '36px',
    },
    section: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    },
    sectionTitle: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#6e6e73',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        margin: '0 0 16px 0',
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px 24px',
    },
    field: { marginBottom: '12px' },
    fieldLabel: {
        display: 'block',
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868b',
        marginBottom: '2px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    fieldValue: {
        display: 'block',
        fontSize: '15px',
        color: '#1d1d1f',
    },
    empty: { color: '#c7c7cc', fontStyle: 'italic' },
    addressText: {
        fontSize: '15px',
        color: '#1d1d1f',
        margin: 0,
        lineHeight: 1.6,
    },
    notesText: {
        fontSize: '15px',
        color: '#3c3c43',
        margin: 0,
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
    },
    fieldRow: { display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' },
    fieldStack: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' },
    fieldGrow: { flex: 1, minWidth: 0 },
    fieldFixed: { width: '140px', flexShrink: 0 },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
        letterSpacing: '-0.1px',
    },
    req: { color: '#c0392b' },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    select: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
        cursor: 'pointer',
    },
    textarea: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '100px',
    },
    editActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '32px',
    },
    cancelButton: {
        padding: '12px 24px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        color: '#3c3c43',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    saveButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    deleteButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,59,48,0.08)',
        color: '#c0392b',
        border: '1px solid rgba(255,59,48,0.2)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '36px',
    },
    deleteConfirmButton: {
        padding: '10px 20px',
        backgroundColor: '#c0392b',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '36px',
    },
};
//# sourceMappingURL=SupplierDetail.js.map